import React, { useCallback, useEffect, useState } from 'react';
import { AiOutlineEdit }                from 'react-icons/ai';
import { MdClose, MdDeleteForever }     from 'react-icons/md';

import Dialog                           from '../Util/Dialog';
import Notice                           from '../Util/Notice';
import userLoggedInHasRole              from '../Util/userLoggedInHasRole';
import WebAPIProblem                    from '../Util/WebAPIProblem';
import { getPartyWorkingHours, saveWorkingHours, deleteWorkingHours, WORKING_HOURS_IMPACT }
                                        from './workingHoursApi';
import { DAY_NAMES, DAY_ORDER, MAX_BLOCKS_PER_DAY, addDays, describeDays,
         patternInEffect, toDateString, validateDays }
                                        from './workingHoursPattern';

const newBlock = dayOfWeek => ({ DayOfWeek: dayOfWeek, StartTime: '09:00', FinishTime: '17:00' });

/**
 * Overlay for viewing and editing one person's working hours: the pattern in
 * effect today, the full history, and (for Diary Managers) adding a pattern
 * from a future date or deleting a future one. Working hours are display only
 * for now; they do not restrict availability, tasks or bookings.
 * @param {object}   api
 * @param {number}   partyID   the person
 * @param {string}   name      the person's name, for the heading
 * @param {function} done      close the overlay; passed true if anything was saved
 */
const WorkingHoursOverlay = ({ api, partyID, name, done }) => {

    const canEdit = userLoggedInHasRole(api, 'Diary Manager');
    const today = toDateString(new Date());

    const [patterns, setPatterns]           = useState(null);
    const [loadError, setLoadError]         = useState(null);
    const [notice, setNotice]               = useState(null);
    const [effectiveFrom, setEffectiveFrom] = useState(toDateString(addDays(new Date(), 1)));
    const [days, setDays]                   = useState(null);
    const [busy, setBusy]                   = useState(false);
    const [changed, setChanged]             = useState(false);
    // the pattern being edited, or null when adding a new change
    const [editing, setEditing]             = useState(null);

    const showPatterns = useCallback(list => {
        setPatterns(list);
        // start the editor from the latest pattern so a change is a small edit
        setDays(prev => prev ?? (list.length ? list[list.length - 1].Days.map(d => ({ ...d })) : []));
    }, []);

    useEffect(() => {
        getPartyWorkingHours(api, partyID).then(({ data, error }) => {
            if (error) {
                setLoadError(error);
            } else {
                showPatterns(data?.Patterns ?? []);
            }
        });
    }, [api, partyID, showPatterns]);

    const current = patterns ? patternInEffect(patterns, today) : null;

    const updateBlock = (index, field, value) =>
        setDays(days.map((d, i) => i === index ? { ...d, [field]: value } : d));

    const removeBlock = index => setDays(days.filter((_, i) => i !== index));

    // the day waiting for the user to confirm a second block, or null
    const [confirmSecondBlock, setConfirmSecondBlock] = useState(null);

    const blockCount = dayOfWeek => days.filter(d => d.DayOfWeek === dayOfWeek).length;

    const appendBlock = dayOfWeek => setDays([...days, newBlock(dayOfWeek)]);

    /* A second block is unusual, as a break such as lunch is better blocked
     * out with a template, so it needs confirming */
    const addBlock = dayOfWeek => {
        if (blockCount(dayOfWeek) > 0) {
            setConfirmSecondBlock(dayOfWeek);
        } else {
            appendBlock(dayOfWeek);
        }
    };

    /* Load a period into the form. A future period is replaced in place (the
     * server replaces a pattern saved on the same start date); one that has
     * already started can only change from today, and either way the change
     * still ends where the next period starts. */
    const startEdit = pattern => {
        setEditing(pattern);
        setDays(pattern.Days.map(d => ({ ...d })));
        setEffectiveFrom(pattern.EffectiveFrom > today ? pattern.EffectiveFrom : today);
        setNotice(null);
    };

    const startNewChange = () => {
        setEditing(null);
        setEffectiveFrom(toDateString(addDays(new Date(), 1)));
        setNotice(null);
    };

    /* Periods that are running or still to come; past ones can't be changed */
    const isEditable = p => !p.EffectiveTo || p.EffectiveTo >= today;

    const handleSave = async () => {
        const problem = effectiveFrom < today
            ? 'The change must start today or later'
            : validateDays(days);
        if (problem) {
            setNotice({ text: problem, isError: true });
            return;
        }
        setBusy(true);
        // B7 (later): the server will reject a save that leaves future
        // availability, tasks or bookings outside the new hours, listing them.
        const { error } = await saveWorkingHours(api, partyID, effectiveFrom, days);
        if (error) {
            setNotice({ text: 'Working hours not saved: ' + error.message, isError: true });
        } else {
            const reload = await getPartyWorkingHours(api, partyID);
            if (!reload.error) {
                setPatterns(reload.data?.Patterns ?? []);
            }
            setChanged(true);
            setEditing(null);
            setNotice({ text: `Working hours saved from ${effectiveFrom}`, isError: false });
        }
        setBusy(false);
    };

    const handleDelete = async workingHoursID => {
        setBusy(true);
        const { data, error } = await deleteWorkingHours(api, workingHoursID);
        if (error) {
            setNotice({ text: 'Working hours not deleted: ' + error.message, isError: true });
        } else {
            setPatterns(data?.Patterns ?? []);
            setChanged(true);
            if (editing?.WorkingHoursID === workingHoursID) {
                startNewChange();
            }
            setNotice({ text: 'Future working hours deleted', isError: false });
        }
        setBusy(false);
    };

    const footer = canEdit && days && patterns && <>
        {editing &&
            <button type='button' className='chronos-button chronos-button--secondary'
                onClick={startNewChange} >Cancel edit</button>}
        <button type='button' className='chronos-button' disabled={busy} onClick={handleSave} >Save</button>
    </>;

    return (
        <Dialog title={`Working hours: ${name}`} onClose={() => done(changed)} footer={footer}
            className='working-hours' >
            <Notice notice={notice} onDismiss={() => setNotice(null)} />

            {loadError ? <WebAPIProblem error={loadError} impact={WORKING_HOURS_IMPACT} />
            : null === patterns ? <p>Loading…</p> : <>
                <p className='working-hours__today'>
                    <strong>Today: </strong>
                    {current ? describeDays(current.Days) : 'No working hours set (not restricted)'}
                </p>

                <h3 className='chronos-dialog__heading'>History</h3>
                {patterns.length ?
                    <table className='chronos-table working-hours__history'>
                        <thead>
                            <tr>
                                <th scope='col'>From</th>
                                <th scope='col'>To</th>
                                <th scope='col'>Hours</th>
                                {canEdit && <th scope='col'><span className='visually-hidden'>Actions</span></th>}
                            </tr>
                        </thead>
                        <tbody>
                            {patterns.map(p =>
                                <tr key={p.WorkingHoursID}
                                    className={p === current ? 'working-hours__current' : undefined} >
                                    <td className='chronos-table__date'>
                                        {formatDate(p.EffectiveFrom)}
                                        {p === current && <span className='chronos-badge'>Current</span>}
                                    </td>
                                    <td className='chronos-table__date'>
                                        {p.EffectiveTo ? formatDate(p.EffectiveTo) : 'onwards'}
                                    </td>
                                    <td><DayList days={p.Days} /></td>
                                    {canEdit && <td className='chronos-table__actions'>
                                        {isEditable(p) &&
                                            <button type='button' className='chronos-icon-button' disabled={busy}
                                                aria-label={`Edit period from ${formatDate(p.EffectiveFrom)}`}
                                                title='Edit' onClick={() => startEdit(p)} >
                                                <AiOutlineEdit aria-hidden='true' />
                                            </button>}
                                        {p.EffectiveFrom > today &&
                                            <button type='button' className='chronos-icon-button chronos-icon-button--danger'
                                                disabled={busy}
                                                aria-label={`Delete period from ${formatDate(p.EffectiveFrom)}`}
                                                title='Delete' onClick={() => handleDelete(p.WorkingHoursID)} >
                                                <MdDeleteForever aria-hidden='true' />
                                            </button>}
                                    </td>}
                                </tr>
                            )}
                        </tbody>
                    </table>
                    : <p>None</p>}

                {canEdit && days &&
                    <section className='working-hours__edit' aria-labelledby='working-hours-edit-heading'>
                        <h3 id='working-hours-edit-heading' className='chronos-dialog__heading'>
                            {editing
                                ? `Edit hours for the period from ${formatDate(editing.EffectiveFrom)}`
                                : 'Change working hours'}
                        </h3>
                        {editing && <p className='chronos-hint'>
                            {editing.EffectiveFrom > today
                                ? 'Saving replaces this period.'
                                : 'This period has started, so the change applies from today.'}
                            {editing.EffectiveTo && ` It still ends on ${formatDate(editing.EffectiveTo)}.`}
                        </p>}
                        <label className='chronos-field'>
                            <span className='chronos-field__label'>From</span>
                            <input type='date' value={effectiveFrom} min={today}
                                onChange={e => setEffectiveFrom(e.target.value)} />
                        </label>
                        <div className='working-hours__days'>
                            {DAY_ORDER.map(dow =>
                                <div key={dow} className='working-hours__day'>
                                    <span className='working-hours__day-name'>{DAY_NAMES[dow]}</span>
                                    <div className='working-hours__blocks'>
                                        {days.map((d, i) => d.DayOfWeek !== dow ? null :
                                            <span key={i} className='working-hours__block'>
                                                <input type='time' aria-label={`${DAY_NAMES[dow]} start`}
                                                    value={d.StartTime} onChange={e => updateBlock(i, 'StartTime', e.target.value)} />
                                                <span aria-hidden='true'>to</span>
                                                <input type='time' aria-label={`${DAY_NAMES[dow]} finish`}
                                                    value={d.FinishTime} onChange={e => updateBlock(i, 'FinishTime', e.target.value)} />
                                                <button type='button' className='chronos-icon-button chronos-icon-button--small'
                                                    aria-label={`Remove ${DAY_NAMES[dow]} block`} title='Remove'
                                                    onClick={() => removeBlock(i)} >
                                                    <MdClose aria-hidden='true' />
                                                </button>
                                            </span>
                                        )}
                                        {blockCount(dow) < MAX_BLOCKS_PER_DAY &&
                                            <button type='button' className='chronos-button chronos-button--secondary chronos-button--small'
                                                aria-label={`Add ${DAY_NAMES[dow]} block`}
                                                onClick={() => addBlock(dow)} >Add block</button>}
                                    </div>
                                </div>
                            )}
                        </div>
                    </section>
                }
            </>}

            {null !== confirmSecondBlock &&
                <Dialog role='alertdialog' title='Add a second block?'
                    onClose={() => setConfirmSecondBlock(null)}
                    footer={<>
                        <button type='button' className='chronos-button chronos-button--secondary'
                            onClick={() => setConfirmSecondBlock(null)} >Cancel</button>
                        <button type='button' className='chronos-button'
                            onClick={() => {
                                appendBlock(confirmSecondBlock);
                                setConfirmSecondBlock(null);
                            }} >Add second block</button>
                    </>} >
                    <p>
                        Are you sure you want more than one block of working time on
                        one day? You can use templates to block out periods like lunch
                        time.
                    </p>
                </Dialog>
            }
        </Dialog>
    );
};

const formatDate = dateStr =>
    new Date(dateStr + 'T00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

/* One line per working day, so the rows of the history line up */
const DayList = ({ days = [] }) => {
    const lines = DAY_ORDER
        .map(dow => ({
            dow,
            blocks: days.filter(d => d.DayOfWeek === dow)
                .sort((a, b) => a.StartTime.localeCompare(b.StartTime)),
        }))
        .filter(l => l.blocks.length);
    if (!lines.length) {
        return 'No working days';
    }
    return (
        <ul className='working-hours__day-list'>
            {lines.map(l =>
                <li key={l.dow}>
                    <span className='working-hours__day-short'>{DAY_NAMES[l.dow].slice(0, 3)}</span>
                    {l.blocks.map(b => `${b.StartTime}–${b.FinishTime}`).join(', ')}
                </li>
            )}
        </ul>
    );
};

export default WorkingHoursOverlay;
